import { style, styleVariants } from "@vanilla-extract/css";
import {
  primitiveTokens,
  semanticThemeTokens
} from "../../theme/ThemeTokens.css";

const { select } = semanticThemeTokens;

/**
 * Filter-bar trigger overrides on top of the base `selectTrigger`:
 *
 * 1. Tighter vertical padding — the two-row label+value stack needs its
 *    own whitespace budget and the base 8px top/bottom makes the chip
 *    feel too tall.
 * 2. No active-state background tint. The eyebrow label + value stack is
 *    itself the signal that a value is set; a background change would be
 *    noisy and would render asymmetrically between light and dark themes
 *    (strong purple tint on light, almost-invisible on dark).
 *
 * Width is inherited from `selectTrigger` so the trigger behaves
 * identically inside a `SelectGroup` (each chip fills its equal flex
 * slot) and standalone (fills its parent).
 */
export const labeledTrigger = style({
  paddingLeft: primitiveTokens.sizing["3x"],
  paddingRight: primitiveTokens.sizing["2x"],
  paddingTop: 2,
  paddingBottom: 2,
  gap: primitiveTokens.sizing["2x"]
});

export const labeledTriggerSizes = styleVariants({
  sm: {
    // sm chips match the base MultiSelect's 32px height (`sizing.8x`)
    // exactly — the two-row content fits tightly inside once we tighten
    // the line-heights below.
    height: primitiveTokens.sizing["8x"],
    fontSize: primitiveTokens.type.xs
  },
  md: {
    height: primitiveTokens.sizing["10x"],
    fontSize: primitiveTokens.type.sm
  }
});

/**
 * Inner two-row stack: eyebrow on top, value on the bottom.
 * Must shrink (`min-width: 0`) so the right-side chrome is never crowded
 * out — the chrome has `flex-shrink: 0` and gets its space first.
 */
export const labeledStack = style({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  gap: 0,
  minWidth: 0,
  flex: "1 1 auto",
  overflow: "hidden",
  textAlign: "left"
});

/**
 * Eyebrow label. Uses `select.default.textColor` — the trigger's muted /
 * placeholder token (neutral[400] on dark, neutral[600] on light) — so the
 * label reads as secondary without needing opacity. Opacity-based dimming
 * at this size muddies subpixel rendering; a naturally-grey token stays
 * crisp. Weight 500 keeps it calmer than the bolder value text below.
 */
export const eyebrowLabel = style({
  fontWeight: 500,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: select.default.textColor,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
  selectors: {
    "button:disabled &, [data-disabled] &": {
      color: select.disabled.textColor
    }
  }
});

export const eyebrowLabelSizes = styleVariants({
  sm: {
    fontSize: 10,
    lineHeight: "12px"
  },
  md: {
    fontSize: 11,
    lineHeight: "14px"
  }
});

/**
 * Value row — holds value(s) + "+N" overflow, or placeholder text.
 */
export const valueRow = style({
  display: "flex",
  alignItems: "center",
  gap: primitiveTokens.sizing["1x"],
  minWidth: 0,
  overflow: "hidden",
  whiteSpace: "nowrap",
  width: "100%"
});

export const valueRowSizes = styleVariants({
  sm: {
    fontSize: primitiveTokens.type.xs,
    lineHeight: "14px"
  },
  md: {
    fontSize: primitiveTokens.type.sm,
    lineHeight: "18px"
  }
});

/**
 * Inline value text. Uses `select.default.textColorSelected` — the same
 * token the base `selectTrigger` applies to its own selected value — so
 * contrast is correct against the trigger background in both themes
 * (purple[700] on light, pure white on dark).
 */
export const valueText = style({
  color: select.default.textColorSelected,
  fontWeight: 500,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
  flex: "0 1 auto",
  selectors: {
    "button:disabled &, [data-disabled] &": {
      color: select.disabled.textColorSelected
    }
  }
});

export const fittedValueText = style({
  flex: "0 0 auto"
});

/**
 * Placeholder — shown in the value row when no value is selected. Uses
 * the muted `textColor` token so the empty state reads as secondary
 * compared to an active value, without changing the trigger's height.
 */
export const placeholderText = style({
  color: select.default.textColor,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
});

/**
 * Overflow "+N" pill. Reserved width so it never gets truncated.
 * Uses the trigger's own selected-text colour for consistency with the
 * inline values; the background stays as the menu-list hover tint, which
 * reads as a subtle purple chip on both light and dark themes.
 */
export const overflowBadge = style({
  flexShrink: 0,
  fontSize: "inherit",
  lineHeight: 1,
  padding: "2px 6px",
  borderRadius: primitiveTokens.radii.xs,
  backgroundColor: select.menuList.listItem.hover.backgroundColor,
  color: select.default.textColorSelected,
  fontWeight: 500
});
